import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Building2, Users, FileText, Wallet, Menu, X, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import Clients from "@/components/superadmin/Clients";
import Profiles from "@/components/superadmin/Profiles";
import Templates from "@/components/superadmin/Templates";
import Wallets from "@/components/superadmin/Wallets";
import SuperAdminIssues from "@/components/superadmin/SuperAdminIssues";

const sidebarItems = [
  { id: "clients", label: "Clients", icon: Building2 },
  { id: "profiles", label: "Profiles", icon: Users },
  { id: "templates", label: "Templates", icon: FileText },
  { id: "wallets", label: "Wallets", icon: Wallet },
  { id: "issues", label: "Reported Issues", icon: AlertCircle },
];

const SuperAdmin = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [active, setActive] = useState(() => {
    const fromUrl = searchParams.get("tab");
    return sidebarItems.find((i) => i.id === fromUrl) ? fromUrl! : sidebarItems[0].id;
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const activeItem = sidebarItems.find((i) => i.id === active);

  const selectTab = (id: string) => {
    setActive(id);
    setSidebarOpen(false);
    setSearchParams((prev) => { prev.set("tab", id); return prev; });
  };

  return (
    <div className="flex flex-1 min-h-0 relative overflow-hidden">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/30 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={cn(
          "fixed inset-y-0 left-0 z-40 transition-transform duration-300",
          "md:relative md:inset-auto md:z-auto md:translate-x-0 md:flex md:shrink-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <aside className="flex h-full w-56 flex-col border-r border-border bg-sidebar">
          <div className="flex items-center justify-between px-4 py-3">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Super Admin
            </h2>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 md:hidden"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <nav className="flex-1 space-y-1 px-2">
            {sidebarItems.map((item) => (
              <button
                key={item.id}
                onClick={() => selectTab(item.id)}
                className={cn(
                  "flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                  active === item.id
                    ? "bg-sidebar-accent text-sidebar-accent-foreground"
                    : "text-sidebar-foreground hover:bg-sidebar-accent/50"
                )}
              >
                <item.icon className="h-4 w-4" />
                {item.label}
              </button>
            ))}
          </nav>
        </aside>
      </div>

      {/* Main content */}
      <div className="flex flex-1 flex-col min-h-0 overflow-hidden">
        {/* Mobile toolbar */}
        <div className="flex items-center gap-2 border-b border-border px-3 py-2 md:hidden shrink-0">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </Button>
          <span className="text-sm font-medium">{activeItem?.label}</span>
        </div>

        {active === "clients" ? (
          <Clients />
        ) : active === "profiles" ? (
          <Profiles />
        ) : active === "templates" ? (
          <Templates />
        ) : active === "wallets" ? (
          <Wallets />
        ) : active === "issues" ? (
          <SuperAdminIssues />
        ) : null}
      </div>
    </div>
  );
};

export default SuperAdmin;
